import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { Folder, ArrowBack, Email, Lock, Visibility, VisibilityOff, Download, Description, VerifiedUser, CloudUpload, Person } from '@mui/icons-material';
import PublicNavbar from '../../../components/public/PublicNavbar';
import { extractErrorMessage } from '../../../utils/errorUtils';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const externalApi = axios.create({ baseURL: API_URL });

const ExternalPortal = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'list' | 'detail'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [token, setToken] = useState('');
  const [proceedings, setProceedings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signDoc, setSignDoc] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signatureCounts, setSignatureCounts] = useState({});
  const [signaturesDialogDoc, setSignaturesDialogDoc] = useState(null);
  const [signaturesDialogData, setSignaturesDialogData] = useState([]);
  const [signaturesLoading, setSignaturesLoading] = useState(false);

  const loadProfile = async (t) => {
    try {
      const res = await externalApi.get('/external/profile', {
        headers: { Authorization: `Bearer ${t}` },
      });
      setProfile(res.data.data);
    } catch {}
  };

  const handleUploadSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSignature(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await externalApi.post('/external/upload-signature', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setProfile(prev => ({ ...prev, signature: res.data.data.key, signatureUrl: res.data.data.url }));
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleSignDocument = async () => {
    if (!signDoc || !selected) return;
    setSigning(true);
    try {
      await externalApi.post(
        `/external/proceedings/${selected.id}/documents/${signDoc.id}/sign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSignDialogOpen(false);
      setSignDoc(null);
      // Reload signature counts
      loadSignatureCounts(selected, token);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSigning(false);
    }
  };

  const loadSignatureCounts = async (proc, t) => {
    if (!proc?.documents) return;
    const counts = {};
    await Promise.all(
      proc.documents.map(async (doc) => {
        try {
          const res = await externalApi.get(
            `/external/proceedings/${proc.id}/documents/${doc.id}/signatures`,
            { headers: { Authorization: `Bearer ${t}` } }
          );
          if (res.data.data?.length > 0) counts[doc.id.toString()] = res.data.data.length;
        } catch {}
      })
    );
    setSignatureCounts(counts);
  };

  const handleViewSignatures = async (doc) => {
    setSignaturesDialogDoc(doc);
    setSignaturesLoading(true);
    try {
      const res = await externalApi.get(
        `/external/proceedings/${selected.id}/documents/${doc.id}/signatures`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSignaturesDialogData(res.data.data || []);
    } catch {
      setSignaturesDialogData([]);
    } finally {
      setSignaturesLoading(false);
    }
  };

  const requestOtp = async () => {
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      await externalApi.post('/external/request-otp', { email });
      setStep('otp');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp) return;
    setLoading(true);
    setError('');
    try {
      const res = await externalApi.post('/external/verify-otp', { email, code: otp });
      const t = res.data.token;
      setToken(t);
      // Load proceedings and profile
      const [procRes] = await Promise.all([
        externalApi.get('/external/proceedings', {
          headers: { Authorization: `Bearer ${t}` },
        }),
        loadProfile(t),
      ]);
      setProceedings(procRes.data.data || []);
      setStep('list');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    setLoading(true);
    setError('');
    try {
      const res = await externalApi.get(`/external/proceedings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const proc = res.data.data;
      setSelected(proc);
      setStep('detail');
      loadSignatureCounts(proc, token);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loanLabel = { custody: 'En custodia', loaned: 'Prestado', returned: 'Devuelto' };

  return (
    <>
      <PublicNavbar />
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        {/* Header azul decorativo */}
      <Box
        sx={{
          background: 'linear-gradient(155deg, #0F172A 0%, #1a3054 45%, #1D4ED8 100%)',
          py: 4,
          px: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.05)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        />
        
        <Box sx={{ maxWidth: 900, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Folder sx={{ fontSize: 32, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', letterSpacing: '-0.02em' }}>
                Portal de Expedientes
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9375rem' }}>
                Acceso seguro a tus documentos
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, py: 4 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4,
            borderRadius: 2,
            background: '#ffffff',
            border: '1px solid #E2E8F0',
          }}
        >

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Step 1: Email */}
        {step === 'email' && (
          <Box>
            <Typography sx={{ color: '#64748B', fontSize: '0.9375rem', mb: 3 }}>
              Ingresa tu correo electrónico para recibir un código de acceso.
            </Typography>
            <TextField
              label="Correo electrónico"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && requestOtp()}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={requestOtp}
              disabled={loading || !email}
              sx={{ py: 1.5, fontWeight: 600 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Enviar código'}
            </Button>
          </Box>
        )}

        {/* Step 2: OTP */}
        {step === 'otp' && (
          <Box>
            <Typography sx={{ color: '#64748B', fontSize: '0.9375rem', mb: 1 }}>
              Ingresa el código de 6 dígitos enviado a
            </Typography>
            <Typography sx={{ mb: 3, fontWeight: 600, color: '#0F172A' }}>
              {email}
            </Typography>
            <TextField
              label="Código de acceso"
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verifyOtp()}
              inputProps={{ 
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem', fontWeight: 600 }
              }}
              sx={{ mb: 2 }}
            />
            <Button 
              variant="contained" 
              fullWidth 
              size="large"
              onClick={verifyOtp} 
              disabled={loading || otp.length < 6}
              sx={{ py: 1.5, fontWeight: 600, mb: 1 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Verificar'}
            </Button>
            <Button fullWidth onClick={() => { setStep('email'); setError(''); }}>
              Volver
            </Button>
          </Box>
        )}

        {/* Step 3: Proceedings list */}
        {step === 'list' && (
          <Box>
            {/* Signature profile section */}
            <Box sx={{ mb: 4, p: 3, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  <Typography fontWeight={600} sx={{ color: '#0F172A' }}>Mi firma electrónica</Typography>
                </Box>
                {profile?.signatureUrl && (
                  <Chip label="Firma registrada" size="small" color="success" variant="outlined" icon={<VerifiedUser />} />
                )}
              </Box>
              {profile?.signatureUrl ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ width: 160, height: 80, border: '1px solid #e0e0e0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
                    <img src={profile.signatureUrl} alt="Mi firma" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </Box>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={uploadingSignature ? <CircularProgress size={14} /> : <CloudUpload />}
                    disabled={uploadingSignature}
                  >
                    {uploadingSignature ? 'Subiendo...' : 'Cambiar firma'}
                    <input type="file" hidden accept="image/*" onChange={handleUploadSignature} />
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Sube una imagen de tu firma para poder firmar documentos electrónicamente.
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    size="small"
                    startIcon={uploadingSignature ? <CircularProgress size={14} color="inherit" /> : <CloudUpload />}
                    disabled={uploadingSignature}
                  >
                    {uploadingSignature ? 'Subiendo...' : 'Subir firma'}
                    <input type="file" hidden accept="image/*" onChange={handleUploadSignature} />
                  </Button>
                </Box>
              )}
            </Box>

            <Typography variant="h6" fontWeight={700} sx={{ color: '#0F172A', mb: 0.5 }}>
              Expedientes compartidos
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: '0.9375rem', mb: 3 }}>
              Selecciona un expediente para ver sus documentos
            </Typography>
            {proceedings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Folder sx={{ fontSize: 64, color: '#CBD5E1', mb: 2 }} />
                <Typography sx={{ color: '#64748B', fontSize: '1rem' }}>No tienes expedientes compartidos.</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {proceedings.map((p) => (
                  <Grid item xs={12} sm={6} key={p.id}>
                    <Card 
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        height: '100%',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: '#F8FAFC',
                          transform: 'translateY(-2px)',
                          boxShadow: 1,
                        },
                      }}
                    >
                      <CardActionArea onClick={() => loadDetail(p.id)} sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                bgcolor: '#EEF2FF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Folder sx={{ fontSize: 24, color: '#6366F1' }} />
                            </Box>
                            <Box flex={1} sx={{ minWidth: 0 }}>
                              <Typography fontWeight={600} sx={{ color: '#0F172A', fontSize: '1rem', mb: 0.5 }}>
                                {p.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748B', mb: 0.5 }}>
                                {p.code}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                {p.company?.name}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={loanLabel[p.loan] || p.loan}
                            size="small"
                            color={p.loan === 'loaned' ? 'warning' : 'default'}
                            sx={{ fontWeight: 500 }}
                          />
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Step 4: Proceeding detail */}
        {step === 'detail' && selected && (
          <Box>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => { setStep('list'); setSelected(null); }}
              sx={{ mb: 3 }}
            >
              Volver
            </Button>
            <Box 
              sx={{ 
                mb: 3, 
                p: 3, 
                borderRadius: 2,
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#EEF2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Folder sx={{ fontSize: 24, color: '#6366F1' }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#0F172A', mb: 0.5 }}>
                    {selected.name}
                  </Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.9375rem' }}>
                    {selected.code} · {selected.company?.name}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A', mb: 3 }}>
                  Documentos
                </Typography>
                {!selected.documents || selected.documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Description sx={{ fontSize: 64, color: '#CBD5E1', mb: 2 }} />
                    <Typography sx={{ color: '#64748B', fontSize: '1rem' }}>Sin documentos disponibles</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {selected.documents.map((doc) => (
                      <Grid item xs={12} key={doc.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2.5,
                            border: '1px solid #E2E8F0',
                            borderRadius: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#CBD5E1',
                              bgcolor: '#F8FAFC',
                              boxShadow: 1,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: '#EEF2FF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Description sx={{ color: '#6366F1', fontSize: 20 }} />
                            </Box>
                            <Typography 
                              sx={{ 
                                color: '#0F172A', 
                                fontSize: '0.9375rem', 
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {doc.file_original_name || doc.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                            {signatureCounts[doc.id.toString()] > 0 && (
                              <Chip
                                icon={<VerifiedUser sx={{ fontSize: 14 }} />}
                                label={`${signatureCounts[doc.id.toString()]} firma${signatureCounts[doc.id.toString()] > 1 ? 's' : ''}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={() => handleViewSignatures(doc)}
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                            <Button
                              variant="outlined"
                              color="success"
                              size="small"
                              startIcon={<VerifiedUser />}
                              onClick={() => {
                                if (!profile?.signature) {
                                  setError('Debe subir su firma antes de firmar un documento. Use la sección "Mi firma electrónica" arriba.');
                                  setStep('list');
                                  return;
                                }
                                setSignDoc(doc);
                                setSignDialogOpen(true);
                              }}
                            >
                              Firmar
                            </Button>
                            {doc.url && (
                              <Button
                                variant="contained"
                                startIcon={<Download />}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                              >
                                Descargar
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        )}
        </Paper>

        {/* Sign Document Dialog */}
        <Dialog open={signDialogOpen} onClose={() => !signing && setSignDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser color="success" />
            Firmar documento
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Estás a punto de firmar electrónicamente el documento:
            </Typography>
            <Typography fontWeight={600} sx={{ mb: 2 }}>
              {signDoc?.file_original_name || signDoc?.name}
            </Typography>
            <Box sx={{ bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 1, p: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {profile?.name} {profile?.lastName || ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {profile?.email}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {new Date().toLocaleString('es-ES')}
              </Typography>
              {profile?.signatureUrl && (
                <Box sx={{ mt: 1, width: 120, height: 50, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                  <img src={profile.signatureUrl} alt="Firma" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </Box>
              )}
            </Box>
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
              Al confirmar, se registrará su firma electrónica conforme al Decreto 2364 de 2012.
              Se capturarán datos de autenticación (IP, dispositivo) para garantizar la integridad del acto.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setSignDialogOpen(false); setSignDoc(null); }} disabled={signing}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSignDocument}
              disabled={signing}
              startIcon={signing ? <CircularProgress size={16} color="inherit" /> : <VerifiedUser />}
            >
              {signing ? 'Firmando...' : 'Confirmar y firmar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Signatures Detail Dialog */}
        <Dialog open={!!signaturesDialogDoc} onClose={() => setSignaturesDialogDoc(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser color="success" />
            Firmas — {signaturesDialogDoc?.file_original_name || signaturesDialogDoc?.name}
          </DialogTitle>
          <DialogContent>
            {signaturesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : signaturesDialogData.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No hay firmas para este documento.
              </Typography>
            ) : (
              signaturesDialogData.map((sig, idx) => (
                <Box key={sig.id?.toString() || idx}>
                  {idx > 0 && <Divider sx={{ my: 2 }} />}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography fontWeight={600}>{sig.signerName}</Typography>
                        <Chip
                          label={sig.signerType === 'internal' ? 'Interno' : 'Externo'}
                          size="small"
                          color={sig.signerType === 'internal' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{sig.signerEmail}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sig.createdAt ? new Date(sig.createdAt).toLocaleString('es-ES') : '—'} · Token: {sig.signatureToken}
                      </Typography>
                    </Box>
                    {sig.signatureImageUrl && (
                      <Box sx={{ width: 120, height: 60, border: '1px solid #e0e0e0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa', flexShrink: 0 }}>
                        <img src={sig.signatureImageUrl} alt="Firma" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      </Box>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSignaturesDialogDoc(null)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Box>
      </Box>
    </>
  );
};

export default ExternalPortal;
